﻿// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Flight upload + archive pipeline (the file uploader and the
// archive/hide flow that mutates the manifest and trend caches):
// - archiveFlightLogSource: POST the archive endpoint for a flight
//   (nothing is deleted; the Archive tab restores it).
// - removeFlightEntryFromList: confirm + archive a flight entry,
//   updating the manifest, hidden/trend Sets, and the selected flight.
// - removeSelectedFlightFromList: trigger removeFlightEntryFromList
//   for the currently-loaded flight.
// - uploadAircraftMetadataParams / uploadUrlForFile: build the POST
//   url + query string for each file.
// - oversizedUploadFile: pre-flight size guard.
// - waitForDatabaseUploadIngest: poll the ingest-job endpoint until
//   the upload finishes parsing.
// - uploadSelectedFiles: the form-submit handler ΓÇö validates the modal,
//   starts a background upload batch, refreshes the manifest as files
//   finish, and opens the newest flight only if the user did not move on.

import { chooseCgrEngineAssignments } from "./cgr-twin-dialog.js";

const MAX_UPLOAD_BYTES = 75 * 1024 * 1024;
// Mirrors MAX_UPLOAD_FILES_PER_SESSION in aircraft-upload-model.ts and the
// server's zip-entry cap (AEROINSIGHT_MAX_UPLOAD_FILES) — one batch may not
// exceed this many files (#190).
const MAX_UPLOAD_FILES = 100;
const UPLOAD_POST_TIMEOUT_MS = 4 * 60 * 1000;
// How many files of one batch are uploaded + ingest-polled at once. Keeps the
// network pipe full instead of paying a full round-trip per file; the server
// bounds actual ingest concurrency separately (MAX_INGEST_CONCURRENCY) and
// same-aircraft ingests still serialize on the per-aircraft advisory lock.
const MAX_CLIENT_UPLOAD_CONCURRENCY = 3;

// Infrastructure-level network failure: the fetch never produced an HTTP
// response. Chrome surfaces these as TypeError ("Failed to fetch") or
// connection-class messages. Application failures (HTTP errors, the 4-minute
// upload abort) deliberately do NOT match.
function transientUploadNetworkError(error) {
  if (error instanceof TypeError) return true;
  return /connection.*(timed out|refused|reset)|ERR_CONNECTION|ERR_NETWORK/i.test(String(error?.message ?? ""));
}

// Minimal p-limit: run `worker(item, index)` over `items` with at most `limit`
// in flight. Workers must handle their own errors — a rejection here would
// abandon the remaining items on that runner.
async function runWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let cursor = 0;
  const runners = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    while (true) {
      const index = cursor;
      cursor += 1;
      if (index >= items.length) return;
      results[index] = await worker(items[index], index);
    }
  });
  await Promise.all(runners);
  return results;
}

export function createUploadFlightPipeline({
  appReadOnlyMode,
  readOnlyActionMessage,
  syncReadOnlyUi,
  databaseFlightArchiveEndpoint,
  databaseUploadEndpoint,
  databaseIngestJobEndpoint,
  flightDisplayTitle,
  flightSourcePathDisplay,
  confirmFlightDeleteDialog,
  revokeCachedFlightReport,
  hideFlightEntry,
  saveHiddenFlightIds,
  saveTrendExcludedFlightIds,
  saveTrendIncludedFlightIds,
  refreshFlightManifestState,
  invalidateAircraftFlightFlagSummaries = () => {},
  clearStoredTrendRowsForAircraft,
  visibleFlightEntries,
  isSupportedFlight,
  flightMatchesSourcePath = (entry, sourcePath) => entry?.path === sourcePath,
  setupFlightLogSelector,
  renderAircraftWorkspace,
  populateTrendAircraftSelect,
  loadFlightLog,
  clearLoadedFlight,
  updateFlightHeader,
  uploadAircraftMetadataSearchParams,
  uploadAircraftTargetValue,
  selectedMonitorFromSelects,
  limitedAircraftModel,
  waitForUploadIngestJob,
  uploadIngestStatusLabel,
  setUploadDialogStatus,
  setUploadDialogError,
  showUploadProgressNotice = () => {},
  uploadFileDisplayName,
  uploadFilesNewestFlightFirst = (files) => files,
  accountWithholdsFlights = async () => false,
  fetchUploadCapacity = async () => null,
  chooseUploadFilesWithinCapacity = async () => null,
  uploadAtFlightLimitMessage = () => "Your plan is at its flight limit. Archive flights or upgrade your plan to upload more.",
  selectedUploadFiles,
  uploadBatchSummary,
  uploadDuplicateSummary,
  shortFlightsSkippedSummary,
  noEngineDataRefusedSummary,
  closeUploadDialog,
  setAircraftPanel,
  openAircraftUploadsPanel = async () => {},
  updateDataSourceLabel,
  loadAircraftUploadHistory = async () => [],
  aircraftUploadHistoryCache,
  aircraftUploadHistoryErrors,
  hiddenFlightIds,
  trendExcludedFlightIds,
  trendIncludedFlightIds,
  getFlightManifest,
  setFlightManifest,
  getSelectedFlight,
  getTrendCache,
  setTrendCache,
  getUploadDialogPostSuccessPanel,
  setUploadDialogPostSuccessPanel,
  browserWindow = globalThis.window,
  maxUploadConcurrency = MAX_CLIENT_UPLOAD_CONCURRENCY,
  uploadRetryDelayMs = 2000,
}) {
  const uploadQueueJobs = [];
  let uploadQueueTail = Promise.resolve();
  let uploadJobSequence = 0;
  let uploadBeforeUnloadGuardActive = false;

  function handleUploadBeforeUnload(event) {
    if (!uploadHasUnsentQueueWork()) return;
    event?.preventDefault?.();
    // Browsers ignore custom beforeunload copy, but setting returnValue is
    // still required for broad support of the native leave-page confirmation.
    if (event) event.returnValue = "";
    return "";
  }

  function syncUploadBeforeUnloadGuard(active) {
    const win = browserWindow;
    if (!win || typeof win.addEventListener !== "function" || typeof win.removeEventListener !== "function") return;
    if (active && !uploadBeforeUnloadGuardActive) {
      win.addEventListener("beforeunload", handleUploadBeforeUnload);
      uploadBeforeUnloadGuardActive = true;
    } else if (!active && uploadBeforeUnloadGuardActive) {
      win.removeEventListener("beforeunload", handleUploadBeforeUnload);
      uploadBeforeUnloadGuardActive = false;
    }
  }

  function uploadJobIsActive(job) {
    return job?.status === "queued" || job?.status === "uploading" || job?.status === "analyzing";
  }

  // "Unsent" = the file's bytes have not reached the server yet. Once a job
  // moves to "analyzing" the ingest job is a persisted server-side row and the
  // server finishes it whether or not this page stays open — so only unsent
  // work should arm the leave-page warning.
  function uploadJobIsUnsent(job) {
    return job?.status === "queued" || job?.status === "uploading";
  }

  function uploadJobIsFinished(job) {
    return job && !uploadJobIsActive(job) && job.status !== "error";
  }

  function uploadHasActiveQueueWork() {
    return uploadQueueJobs.some(uploadJobIsActive);
  }

  function uploadHasUnsentQueueWork() {
    return uploadQueueJobs.some(uploadJobIsUnsent);
  }

  function uploadQueueSnapshot() {
    const visibleJobs = [];
    const seen = new Set();
    const pushVisibleJob = (job) => {
      if (!job || seen.has(job.id) || visibleJobs.length >= 12) return;
      seen.add(job.id);
      visibleJobs.push(job);
    };
    // The popup is upload progress only: files being sent right now, failures
    // that need attention, then a little recent/queued context. Files parked
    // in "analyzing" (uploaded, waiting on the server) are deliberately NOT
    // listed — with a big folder they would flood the panel, and the uploads
    // page is the place to follow analysis progress.
    uploadQueueJobs
      .filter((job) => job?.status === "uploading")
      .forEach(pushVisibleJob);
    uploadQueueJobs
      .filter((job) => job?.status === "error")
      .forEach(pushVisibleJob);
    uploadQueueJobs
      .filter(uploadJobIsFinished)
      .slice(-4)
      .forEach(pushVisibleJob);
    uploadQueueJobs
      .filter((job) => job?.status === "queued")
      .slice(0, 4)
      .forEach(pushVisibleJob);
    uploadQueueJobs
      .filter((job) => job?.status !== "analyzing")
      .slice(-12)
      .forEach(pushVisibleJob);
    return visibleJobs.map((job) => ({
      id: job.id,
      targetAircraft: job.targetAircraft,
      fileName: job.fileName,
      status: job.status,
      statusLabel: job.statusLabel,
      message: job.message,
      error: job.error,
      retryCount: job.retryCount,
      createdAt: job.createdAt,
      updatedAt: job.updatedAt,
    }));
  }

  function uploadQueueSummary() {
    const total = uploadQueueJobs.length;
    const active = uploadQueueJobs.filter(uploadJobIsActive).length;
    const finished = uploadQueueJobs.filter(uploadJobIsFinished).length;
    const failed = uploadQueueJobs.filter((job) => job.status === "error").length;
    const analyzing = uploadQueueJobs.filter((job) => job?.status === "analyzing").length;
    if (analyzing && !uploadHasUnsentQueueWork()) {
      // Every file's bytes are on the server and the ingest jobs are queued
      // there, so from the user's perspective the UPLOAD is done — report it
      // as complete and point at the uploads panel for analysis progress.
      // Closing the page loses nothing from here on.
      return {
        status: "ready",
        title: "Uploads complete",
        message: [
          `${analyzing} flight log${analyzing === 1 ? "" : "s"} still processing on the server.`,
          finished ? `${finished} finished.` : "",
          failed ? `${failed} failed.` : "",
          "You can leave this page — analysis keeps running.",
        ].filter(Boolean).join(" "),
        linkHref: "#uploads",
        linkLabel: "View analysis progress",
      };
    }
    if (active) {
      // Batch header is pure UPLOAD progress — a file counts as soon as its
      // bytes are on the server (analysis is the uploads page's story). The
      // numerator therefore reaches the total exactly when the last POST
      // settles and the header flips to "Uploads complete".
      const uploading = uploadQueueJobs.filter((job) => job?.status === "uploading").length;
      const queued = uploadQueueJobs.filter((job) => job?.status === "queued").length;
      const uploaded = total - uploading - queued;
      const progressTitle = total > 1
        ? `${uploaded} of ${total} uploads completed`
        : "1 upload in progress";
      const progressParts = [
        uploading ? `${uploading} uploading now.` : "",
        queued ? `${queued} still to upload.` : "",
        failed ? `${failed} failed.` : "",
      ].filter(Boolean);
      return {
        status: "loading",
        title: progressTitle,
        message: progressParts.join(" ") || "Preparing uploads...",
      };
    }
    if (failed) {
      return {
        status: "error",
        title: failed === 1 ? "Upload needs attention" : `${failed} uploads need attention`,
        message: "Failed uploads stay here so you can inspect or retry them.",
      };
    }
    return {
      status: "ready",
      title: "Upload queue complete",
      message: "All queued uploads have finished.",
    };
  }

  function notifyUploadQueue({
    status,
    title,
    message,
    targetAircraft = "",
    autoDismissMs = 0,
    forceOpen = false,
  } = {}) {
    const summary = uploadQueueSummary();
    const activeQueueTakesPriority = summary.status === "loading" && status !== "error";
    // The summary's link (e.g. "View analysis progress" while the server
    // drains) belongs to the summary message — only show it when that
    // message is what actually renders.
    const usesSummaryMessage = activeQueueTakesPriority || message === undefined;
    updateUploadProgressNotice({
      status: activeQueueTakesPriority ? summary.status : status ?? summary.status,
      title: activeQueueTakesPriority ? summary.title : title ?? summary.title,
      message: activeQueueTakesPriority ? summary.message : message ?? summary.message,
      linkHref: usesSummaryMessage ? summary.linkHref ?? "" : "",
      linkLabel: usesSummaryMessage ? summary.linkLabel ?? "" : "",
      targetAircraft,
      autoDismissMs,
      forceOpen,
    });
  }

  function setUploadJobState(job, patch = {}, noticeOptions = {}) {
    if (!job) return;
    Object.assign(job, patch, { updatedAt: Date.now() });
    if (!uploadJobIsActive(job) && job.status !== "error") job.file = null;
    syncUploadBeforeUnloadGuard(uploadHasUnsentQueueWork());
    notifyUploadQueue({
      targetAircraft: job.targetAircraft,
      ...noticeOptions,
    });
  }

  function createUploadJob({ file, targetAircraft, uploadUrl, batchId }) {
    const fileName = uploadFileDisplayName(file);
    uploadJobSequence += 1;
    return {
      id: `upload-job-${uploadJobSequence}`,
      batchId,
      file,
      fileName,
      targetAircraft,
      uploadUrl,
      status: "queued",
      statusLabel: "Queued",
      message: "Waiting for upload slot",
      error: "",
      retryCount: 0,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
  }

  function retryUploadQueueJob(jobId) {
    const job = uploadQueueJobs.find((candidate) => candidate.id === jobId);
    if (!job || job.status !== "error" || !job.file) return null;
    job.retryCount += 1;
    const batch = {
      id: `${job.batchId}-retry-${job.retryCount}`,
      jobs: [job],
      targetAircraft: job.targetAircraft,
      initialSelectedFlightId: getSelectedFlight()?.id ?? "",
      postSuccessPanel: "",
    };
    setUploadJobState(job, {
      status: "queued",
      statusLabel: "Queued",
      message: job.retryCount === 1 ? "Retry queued" : `Retry ${job.retryCount} queued`,
      error: "",
    }, {
      title: "Retry queued",
      message: `${job.fileName} will run after earlier uploads.`,
      forceOpen: true,
    });
    return enqueueUploadBatch(batch);
  }

  function enqueueUploadBatch(batch) {
    syncUploadBeforeUnloadGuard(true);
    notifyUploadQueue({
      title: batch.jobs.length === 1 ? "Upload queued" : `${batch.jobs.length} uploads queued`,
      message: batch.jobs.length === 1
        ? `${batch.jobs[0].fileName} is waiting for the upload slot.`
        : `${batch.jobs.length} files for ${batch.targetAircraft} are waiting for the upload slot.`,
      targetAircraft: batch.targetAircraft,
      forceOpen: true,
    });
    const runBatch = () => runQueuedUploadBatch(batch);
    const completion = uploadQueueTail.then(runBatch, runBatch);
    uploadQueueTail = completion.catch(() => {});
    return completion;
  }

  async function postUploadFile(job) {
    const canAbort = typeof AbortController === "function";
    const controller = canAbort ? new AbortController() : null;
    const timerHost = browserWindow && typeof browserWindow.setTimeout === "function" ? browserWindow : globalThis;
    let timeoutId = null;
    if (controller && typeof timerHost.setTimeout === "function") {
      timeoutId = timerHost.setTimeout(() => controller.abort(), UPLOAD_POST_TIMEOUT_MS);
    }
    try {
      return await fetch(job.uploadUrl, {
        method: "POST",
        headers: {
          "content-type": "application/octet-stream",
          ...(job.cgrAssignments ? { "x-cgr-engine-assignments": JSON.stringify(job.cgrAssignments) } : {}),
        },
        body: job.file,
        ...(controller ? { signal: controller.signal } : {}),
      });
    } catch (error) {
      if (error?.name === "AbortError") {
        const minutes = Math.round(UPLOAD_POST_TIMEOUT_MS / 60000);
        throw new Error(`${job.fileName} did not finish uploading within ${minutes} minutes. The queue moved on to the next file.`);
      }
      throw error;
    } finally {
      if (timeoutId !== null && typeof timerHost.clearTimeout === "function") {
        timerHost.clearTimeout(timeoutId);
      } else if (timeoutId !== null && typeof globalThis.clearTimeout === "function") {
        globalThis.clearTimeout(timeoutId);
      }
    }
  }

  async function archiveFlightLogSource(entry) {
    if (appReadOnlyMode()) throw new Error(readOnlyActionMessage("Archiving flight logs"));
    const response = await fetch(databaseFlightArchiveEndpoint(entry.database.flightId), {
      method: "POST",
      headers: { Accept: "application/json" },
    });
    let payload = null;
    try {
      payload = await response.json();
    } catch {}
    if (!response.ok) {
      throw new Error(payload?.error ?? `Unable to archive "${flightDisplayTitle(entry)}".`);
    }
    return payload;
  }

  async function removeFlightEntryFromList(entry, { triggerButton = null } = {}) {
    if (!entry) return;
    if (appReadOnlyMode()) {
      window.alert(readOnlyActionMessage("Archiving flights"));
      syncReadOnlyUi();
      return;
    }
    const selectedFlight = getSelectedFlight();
    const entriesExpectedToBeRemoved = [entry];
    const removesSelectedFlight = Boolean(selectedFlight?.id)
      && entriesExpectedToBeRemoved.some((item) => item.id === selectedFlight.id);
    const confirmed = await confirmFlightDeleteDialog({
      // Archiving deletes nothing, so the deleted-flight retention
      // disclosure (issue #405) does NOT apply here; it lives on the
      // Archive tab's permanent-delete confirmation instead.
      title: `Archive ${flightDisplayTitle(entry)}?`,
      eyebrow: "Archive Flight",
      copy: `"${flightDisplayTitle(entry)}" moves to the Archive tab in My Account and disappears from this list.`,
      details: `Nothing is deleted. The uploaded log (${flightSourcePathDisplay(entry)}) and all analysis stay stored, the flight stops counting toward your plan limit, and you can restore it from the Archive tab whenever your plan has room.`,
      warningTitle: "You can restore it anytime.",
      confirmLabel: "Archive Flight",
      destructive: false,
    });
    if (!confirmed) return;
    const headerButton = document.querySelector("#action-remove-flight");
    if (headerButton) headerButton.disabled = true;
    if (triggerButton) triggerButton.disabled = true;
    try {
      revokeCachedFlightReport(entry.id);
      await archiveFlightLogSource(entry);
      // Keep the account page (Archive tab, aircraft stats) current without
      // a reload; source "workspace" stops the account listener's own
      // workspace refresh from double-running this flow's cleanup below.
      try {
        window.dispatchEvent(new CustomEvent("aeroinsight:archive-changed", { detail: { source: "workspace" } }));
      } catch { /* ignore */ }
      entriesExpectedToBeRemoved.forEach((item) => revokeCachedFlightReport(item.id));
      entriesExpectedToBeRemoved.forEach((item) => {
        hiddenFlightIds.delete(item.id);
        trendExcludedFlightIds.delete(item.id);
        trendIncludedFlightIds.delete(item.id);
      });
      saveHiddenFlightIds(hiddenFlightIds);
      saveTrendExcludedFlightIds(trendExcludedFlightIds);
      saveTrendIncludedFlightIds(trendIncludedFlightIds);
      await refreshFlightManifestState(removesSelectedFlight ? "" : selectedFlight?.id ?? "");
      clearStoredTrendRowsForAircraft(entry.aircraft);
      setTrendCache(new Map());
      const removedSelectedFlight = removesSelectedFlight;
      const replacement = visibleFlightEntries().find((item) => item.id !== entry.id && isSupportedFlight(item));
      setupFlightLogSelector();
      renderAircraftWorkspace();
      if (removedSelectedFlight) {
        populateTrendAircraftSelect(replacement?.aircraft);
        if (replacement) {
          await loadFlightLog(replacement, { markSeen: false });
        } else {
          clearLoadedFlight(new Error("No visible supported flight logs"));
        }
        return;
      }
      populateTrendAircraftSelect(getSelectedFlight()?.aircraft ?? replacement?.aircraft);
      updateFlightHeader();
    } catch (error) {
      window.alert(error.message);
    } finally {
      if (headerButton) headerButton.disabled = !getSelectedFlight();
      if (triggerButton) triggerButton.disabled = false;
    }
  }

  async function removeSelectedFlightFromList() {
    const selectedFlight = getSelectedFlight();
    if (!selectedFlight) return;
    await removeFlightEntryFromList(selectedFlight);
  }

  function uploadAircraftMetadataParams(fileName, file = null) {
    const select = document.querySelector("#uploadAircraftSelect");
    return uploadAircraftMetadataSearchParams({
      filename: fileName,
      relativePath: file?.webkitRelativePath,
      targetAircraft: uploadAircraftTargetValue(),
      targetAircraftId: select?.value === "__new__" ? "" : select?.selectedOptions?.[0]?.dataset?.aircraftId,
      creatingNew: select?.value === "__new__",
      make: document.querySelector("#uploadAircraftMakeInput")?.value,
      model: document.querySelector("#uploadAircraftModelInput")?.value,
      engine: document.querySelector("#uploadAircraftEngineInput")?.value,
      propellerType: document.querySelector("#uploadAircraftPropellerInput")?.value,
      monitor: selectedMonitorFromSelects(
        document.querySelector("#uploadAircraftMonitorMakeInput"),
        document.querySelector("#uploadAircraftMonitorModelInput"),
      ),
      includeShortFlights: Boolean(document.querySelector("#uploadIncludeShortFlights")?.checked),
      limitAircraftModel: limitedAircraftModel,
    });
  }

  function uploadUrlForFile(file) {
    return `${databaseUploadEndpoint}?${uploadAircraftMetadataParams(file.name, file)}`;
  }

  function oversizedUploadFile(files) {
    return files.find((file) => Number(file?.size ?? 0) > MAX_UPLOAD_BYTES) ?? null;
  }

  function updateUploadProgressNotice({
    status = "loading",
    title = "Uploading logs",
    message = "",
    linkHref = "",
    linkLabel = "",
    targetAircraft = "",
    autoDismissMs = 0,
    forceOpen = false,
  } = {}) {
    // The notice reports both upload and server-side analysis progress. A file
    // counts as uploaded once its bytes are on the server, but the notice stays
    // minimizable (not fully closable) until the queued ingest work settles so
    // users keep a visible signal that analysis is still running.
    const unsentCount = uploadQueueJobs.filter(uploadJobIsUnsent).length;
    const activeCount = uploadQueueJobs.filter(uploadJobIsActive).length;
    const processingCount = uploadQueueJobs.filter((job) => job?.status === "analyzing").length;
    showUploadProgressNotice({
      status,
      title,
      message,
      linkHref,
      linkLabel,
      targetAircraft,
      autoDismissMs,
      forceOpen,
      jobs: uploadQueueSnapshot(),
      progress: {
        total: uploadQueueJobs.length,
        uploaded: uploadQueueJobs.length - unsentCount,
        settled: uploadQueueJobs.length - activeCount,
        active: activeCount,
        processing: processingCount,
      },
      working: activeCount > 0,
      onRetryJob: (jobId) => retryUploadQueueJob(jobId),
    });
  }

  async function refreshAfterUploadedFile({
    preferredFlightId = "",
    preferredPath = "",
    targetAircraft = "",
  } = {}) {
    const preferredFlight = await refreshFlightManifestState(preferredFlightId, preferredPath);
    const preferredAircraftId = preferredFlight?.database?.aircraftId;
    if (preferredAircraftId) {
      const aircraftEntries = getFlightManifest().filter((entry) => entry?.database?.aircraftId === preferredAircraftId);
      invalidateAircraftFlightFlagSummaries(preferredAircraftId, aircraftEntries);
    }
    setupFlightLogSelector();
    renderAircraftWorkspace();
    populateTrendAircraftSelect(getSelectedFlight()?.aircraft ?? preferredFlight?.aircraft ?? targetAircraft);
    updateDataSourceLabel(true);
    const uploadedFlights = getFlightManifest().filter((entry) => (
      (preferredFlightId && entry.id === preferredFlightId)
      || (preferredPath && flightMatchesSourcePath(entry, preferredPath))
    ));
    const selectableUploadedFlights = uploadedFlights.filter((entry) => (
      isSupportedFlight(entry) && entry?.database?.planAccess?.locked !== true
    ));
    const selectableFlight = selectableUploadedFlights.find((entry) => entry.id === preferredFlight?.id)
      ?? selectableUploadedFlights[0]
      ?? (!uploadedFlights.length && preferredFlight?.database?.planAccess?.locked !== true ? preferredFlight : null)
      ?? null;
    return {
      selectableFlight,
      uploadedFlights,
      lockedFlights: uploadedFlights.filter((entry) => entry?.database?.planAccess?.locked === true),
    };
  }

  function retainedLockedFlightsMessage(count) {
    const total = Math.max(0, Number(count) || 0);
    return `${total} flight${total === 1 ? " was" : "s were"} retained but locked by your plan. ${total === 1 ? "It will" : "They will"} unlock automatically when plan capacity becomes available.`;
  }

  async function waitForDatabaseUploadIngest(jobId, {
    stallTimeoutMs = 90000,
    intervalMs = 350,
    onStatus = (job) => setUploadDialogStatus(uploadIngestStatusLabel(job)),
  } = {}) {
    return waitForUploadIngestJob(jobId, {
      fetchJob: (id) => fetch(databaseIngestJobEndpoint(id), { cache: "no-store" }),
      onStatus,
      stallTimeoutMs,
      intervalMs,
      treatMissingAsDiscarded: true,
      wait: (ms) => new Promise((resolve) => window.setTimeout(resolve, ms)),
    });
  }

  async function runQueuedUploadBatch(batch) {
    const { jobs, targetAircraft, creatingNewAircraft, initialSelectedFlightId, postSuccessPanel } = batch;
    let newestMatchingFlight = null;
    let newestUploadedPath = "";
    const duplicateUploads = [];
    const failedUploads = [];
    let uploadedCount = 0;
    let skippedShortFlightTotal = 0;
    let skippedNoEngineDataFlightTotal = 0;
    let refusedNoEngineDataUploadTotal = 0;
    let discardedUnsupportedTotal = 0;
    let analyzedFlightTotal = 0;
    let newestSelectableFlight = null;
    const retainedLockedFlightIds = new Set();
    // Prime the uploads page as soon as the batch's first bytes land. The
    // uploads panel's live loop (scheduleActiveUploadHistoryRefresh, 1.5s)
    // only arms once the client's upload-history cache holds a
    // queued/processing row, and without this kick the first cache refresh
    // happened only after a file's ANALYSIS settled — on a long server queue
    // (or with the notice hidden) the uploads page sat stale until the first
    // ingest finished. One kick per batch is enough: once the queued rows are
    // in the cache the panel's own poller keeps the page live until every job
    // settles, independent of the notice.
    let uploadHistoryPrimed = false;
    async function primeUploadHistoryAfterFirstPost() {
      if (uploadHistoryPrimed) return;
      uploadHistoryPrimed = true;
      try {
        // A brand-new aircraft exists server-side once the first POST is
        // accepted, but the client's manifest predates it — so the fleet
        // sidebar still said "No aircraft yet" and the notice's View-uploads
        // navigation had nowhere to go until the whole batch settled. Refresh
        // the manifest now and land the user on the new aircraft's Uploads
        // page immediately so the batch's progress is watchable from the
        // first file, not the last.
        if (creatingNewAircraft) {
          await refreshAfterUploadedFile({ targetAircraft });
        }
        aircraftUploadHistoryCache.delete(targetAircraft);
        aircraftUploadHistoryErrors.delete(targetAircraft);
        await loadAircraftUploadHistory(targetAircraft, { force: true }).catch(() => []);
        if (creatingNewAircraft) {
          await openAircraftUploadsPanel(targetAircraft);
        }
        renderAircraftWorkspace();
      } catch {
        // Best-effort: the per-file post-ingest refresh still covers the page.
      }
    }
    // Phase 1 — send one file. Returns a record for the analysis-wait phase
    // when the upload landed, or null when the job settled here (network
    // failure, duplicate, ignored). Only POSTs compete for the upload slots:
    // the server queues the ingest job, so the (much longer) analysis wait is
    // deferred to phase 2 and the whole folder reaches the server at network
    // speed instead of at per-file ingest speed. Shared batch counters are
    // plain mutations (single-threaded event loop), and every error is caught
    // here so one bad file can't abandon the remaining runners.
    async function postOneUpload(job, index) {
      const file = job.file;
      const displayName = job.fileName;
      const fileOrdinal = `${index + 1}/${jobs.length}`;
      try {
        setUploadDialogStatus(`Uploading ${fileOrdinal}: ${displayName}...`);
        setUploadJobState(job, {
          status: "uploading",
          statusLabel: "Uploading",
          message: `${fileOrdinal}: sending file`,
          error: "",
        });
        let response = null;
        let payload = null;
        let retriedTransientNetworkError = false;
        let retriedTransientServerError = false;
        while (true) {
          try {
            response = await postUploadFile(job);
            payload = await response.json().catch(() => null);
            if (response.status === 409 && payload?.code === "cgr_engine_assignment" && !job.cgrAssignments) {
              const assignments = await chooseCgrEngineAssignments(payload);
              if (!assignments) throw new Error("Engine assignment canceled. The log was not uploaded.");
              job.cgrAssignments = assignments;
              continue;
            }
            // One automatic retry when the server's per-request access check
            // hiccuped (503 lifecycle_check_failed - a transient DB stall
            // under ingest load, not anything wrong with this file).
            if (response.status === 503 && payload?.code === "lifecycle_check_failed" && !retriedTransientServerError) {
              retriedTransientServerError = true;
              setUploadJobState(job, {
                status: "uploading",
                statusLabel: "Retrying",
                message: `${fileOrdinal}: server busy — retrying`,
                error: "",
              });
              await new Promise((resolve) => (browserWindow?.setTimeout ?? setTimeout)(resolve, uploadRetryDelayMs));
              continue;
            }
            break;
          } catch (error) {
            // One automatic retry for infrastructure-level network failures
            // (Chrome's TypeError "Failed to fetch", connection timed
            // out/reset). A 137-file batch will occasionally lose a single
            // connection; that shouldn't cost the user a manual Retry click.
            // Anything else — including the deliberate 4-minute POST abort —
            // fails the file as before.
            if (!retriedTransientNetworkError && transientUploadNetworkError(error)) {
              retriedTransientNetworkError = true;
              setUploadJobState(job, {
                status: "uploading",
                statusLabel: "Retrying",
                message: `${fileOrdinal}: connection dropped — retrying`,
                error: "",
              });
              await new Promise((resolve) => (browserWindow?.setTimeout ?? setTimeout)(resolve, uploadRetryDelayMs));
              continue;
            }
            const message = error?.message || `Upload failed for ${displayName}.`;
            failedUploads.push({ fileName: displayName, message });
            setUploadJobState(job, {
              status: "error",
              statusLabel: "Failed",
              message,
              error: message,
            }, { forceOpen: true });
            return null;
          }
        }
        if (!response.ok) {
          // Server-side plan cap backstop: label the row clearly instead of a
          // generic failure (the client gate usually catches this first).
          if (response.status === 403 && payload?.reason === "plan_flight_limit") {
            const message = payload?.error || "Your plan is at its flight limit. Archive flights or upgrade your plan to upload more.";
            failedUploads.push({ fileName: displayName, message });
            setUploadJobState(job, {
              status: "error",
              statusLabel: "Plan limit",
              message,
              error: message,
            }, { forceOpen: true });
            return null;
          }
          if (response.status === 409 && payload?.duplicateUpload) {
            const duplicate = { fileName: file.name, payload };
            duplicateUploads.push(duplicate);
            setUploadJobState(job, {
              status: "duplicate",
              statusLabel: "Duplicate",
              message: uploadDuplicateSummary([duplicate]) || "Duplicate upload skipped.",
            });
            return null;
          }
          const message = payload?.error ?? `Upload failed for ${displayName}.`;
          failedUploads.push({ fileName: displayName, message });
          setUploadJobState(job, {
            status: "error",
            statusLabel: "Failed",
            message,
            error: message,
          }, { forceOpen: true });
          return null;
        }
        const uploadNotice = payload?.uploadNotice;
        if (uploadNotice?.discarded === true) {
          discardedUnsupportedTotal += Number(uploadNotice.ignoredCount ?? uploadNotice.discardedCount ?? 1);
        }
        if (uploadNotice?.discarded === true && !payload?.upload && !payload?.ingestJob && !payload?.uploads?.length) {
          const message = "No usable flight was found in that upload, so it was ignored.";
          setUploadJobState(job, {
            status: "ready",
            statusLabel: "Ignored",
            message,
          });
          setUploadDialogStatus(message);
          return null;
        }
        uploadedCount += 1;
        setUploadDialogStatus(`Saved ${displayName}.`);
        setUploadJobState(job, {
          status: "analyzing",
          statusLabel: "Waiting",
          message: `${fileOrdinal}: uploaded — waiting for analysis`,
        });
        // Fire-and-forget: priming must not hold this POST slot open.
        void primeUploadHistoryAfterFirstPost();
        return { job, displayName, fileOrdinal, payload };
      } catch (error) {
        const message = error?.message || `Upload failed for ${displayName}.`;
        failedUploads.push({ fileName: displayName, message });
        setUploadJobState(job, {
          status: "error",
          statusLabel: "Failed",
          message,
          error: message,
        }, { forceOpen: true });
        return null;
      }
    }
    // Phase 2 — wait for one uploaded file's server-side analysis and do the
    // post-ingest bookkeeping. Rows queued behind the server's ingest pool sit
    // in "analyzing" with the server's queue heartbeat keeping the stall
    // detector fed; a job that already finished settles on the first poll.
    async function waitForOneAnalysis({ job, displayName, fileOrdinal, payload }) {
      try {
        setUploadJobState(job, {
          status: "analyzing",
          statusLabel: "Analyzing",
          message: `${fileOrdinal}: processing flight data`,
        });
        let settledJob = null;
        try {
          settledJob = await waitForDatabaseUploadIngest(payload?.ingestJob?.id, {
            onStatus: (ingestJob) => {
              const label = uploadIngestStatusLabel(ingestJob);
              setUploadDialogStatus(label);
              setUploadJobState(job, {
                status: "analyzing",
                statusLabel: "Analyzing",
                message: label,
              });
            },
          });
        } catch (error) {
          const message = error?.message || `Ingest failed for ${displayName}.`;
          failedUploads.push({ fileName: displayName, message });
          uploadedCount -= 1;
          setUploadJobState(job, {
            status: "error",
            statusLabel: "Failed",
            message,
            error: message,
          }, { forceOpen: true });
          return;
        }
        // The short-flight gate runs during ingest, so the settled job tells us
        // whether this upload's flights were analyzed or skipped for being short.
        skippedShortFlightTotal += Number(settledJob?.summary_json?.skippedShortFlightCount ?? 0);
        const noEngineDataFlightCount = Number(settledJob?.summary_json?.skippedNoEngineDataFlightCount ?? 0);
        skippedNoEngineDataFlightTotal += noEngineDataFlightCount;
        if (noEngineDataFlightCount > 0 && Number(settledJob?.summary_json?.analyzedFlightCount ?? 0) === 0) {
          refusedNoEngineDataUploadTotal += 1;
        }
        if (settledJob?.summary_json?.discarded === true) discardedUnsupportedTotal += 1;
        analyzedFlightTotal += Number(settledJob?.summary_json?.analyzedFlightCount ?? 0);
        aircraftUploadHistoryCache.delete(targetAircraft);
        aircraftUploadHistoryErrors.delete(targetAircraft);
        await loadAircraftUploadHistory(targetAircraft, { force: true }).catch(() => []);
        const uploadedPath = payload?.uploadedPath ?? "";
        newestUploadedPath = uploadedPath || newestUploadedPath;
        const newestPairedFlight = payload?.cgrPairing?.inserted
          ?.find((entry) => entry?.supported_status === "supported" || isSupportedFlight(entry))
          ?? payload?.cgrPairing?.inserted?.[0]
          ?? null;
        const fileMatchingFlight = newestPairedFlight
          ?? payload?.flights?.find((entry) => entry.supported_status === "supported")
          ?? payload?.flights?.[0]
          ?? newestMatchingFlight;
        newestMatchingFlight = fileMatchingFlight ?? newestMatchingFlight;
        const refreshResult = await refreshAfterUploadedFile({
          preferredFlightId: fileMatchingFlight?.id ?? "",
          preferredPath: uploadedPath || newestUploadedPath,
          targetAircraft,
        });
        const refreshedFlight = refreshResult.selectableFlight;
        refreshResult.lockedFlights.forEach((entry) => retainedLockedFlightIds.add(entry.id));
        const retainedMessage = refreshResult.lockedFlights.length
          ? retainedLockedFlightsMessage(refreshResult.lockedFlights.length)
          : "";
        setUploadJobState(job, {
          status: "ready",
          statusLabel: refreshedFlight ? "Ready" : retainedMessage ? "Plan limit" : "Saved",
          message: refreshedFlight
            ? `${displayName} is ready.${retainedMessage ? ` ${retainedMessage}` : ""}`
            : retainedMessage || `${displayName} finished processing.`,
        });
        if (refreshedFlight) {
          newestSelectableFlight = refreshedFlight;
        }
      } catch (error) {
        // Unexpected failure (e.g. the post-ingest refresh): record it against
        // this file only; the other waits keep going.
        const message = error?.message || `Upload failed for ${displayName}.`;
        failedUploads.push({ fileName: displayName, message });
        setUploadJobState(job, {
          status: "error",
          statusLabel: "Failed",
          message,
          error: message,
        }, { forceOpen: true });
      }
    }
    try {
      const uploadedRecords = (await runWithConcurrency(jobs, maxUploadConcurrency, postOneUpload)).filter(Boolean);
      await runWithConcurrency(uploadedRecords, maxUploadConcurrency, waitForOneAnalysis);
      const summary = uploadBatchSummary({
        uploaded: uploadedCount,
        duplicates: duplicateUploads.length,
        failed: failedUploads.length,
      });
      if (!uploadedCount && duplicateUploads.length) {
        const message = uploadDuplicateSummary(duplicateUploads) || summary;
        setUploadDialogStatus(message);
        notifyUploadQueue({
          status: "ready",
          title: "Upload skipped",
          message,
          targetAircraft,
          autoDismissMs: 12000,
        });
        return;
      }
      if (failedUploads.length && !uploadedCount) {
        throw new Error(`${summary}. ${failedUploads[0].message}`);
      }
      if (!uploadedCount && discardedUnsupportedTotal > 0) {
        const message = discardedUnsupportedTotal === 1
          ? "No usable flight was found in that upload, so it was ignored."
          : `${discardedUnsupportedTotal} uploads had no usable flight data and were ignored.`;
        setUploadDialogStatus(message);
        notifyUploadQueue({
          status: "ready",
          title: "Upload ignored",
          message,
          targetAircraft,
          autoDismissMs: 12000,
        });
        return;
      }
      if (!uploadedCount) {
        throw new Error(summary);
      }
      // Engine-data refusal wins over the generic completion / "no usable
      // flight" copy: the batch was readable but analyzed nothing, so never
      // announce success or navigate — even when the aircraft's existing
      // flights would give the manifest refresh something to select.
      if (analyzedFlightTotal === 0 && skippedNoEngineDataFlightTotal > 0 && !failedUploads.length) {
        const message = noEngineDataRefusedSummary(refusedNoEngineDataUploadTotal || 1);
        jobs
          .filter((job) => job.status === "ready")
          .forEach((job) => {
            setUploadJobState(job, {
              statusLabel: "No engine data",
              message,
            });
          });
        setUploadDialogStatus(message);
        notifyUploadQueue({
          status: "ready",
          title: "Upload refused",
          message,
          targetAircraft,
          autoDismissMs: 12000,
        });
        return;
      }
      if (!newestSelectableFlight) {
        if (retainedLockedFlightIds.size > 0) {
          const retainedMessage = retainedLockedFlightsMessage(retainedLockedFlightIds.size);
          const message = `${summary}. ${retainedMessage}`;
          setUploadDialogStatus(message);
          notifyUploadQueue({
            status: "ready",
            title: "Upload retained",
            message,
            targetAircraft,
            autoDismissMs: 16000,
          });
          if (postSuccessPanel) {
            try {
              await setAircraftPanel(postSuccessPanel);
            } catch (error) {
              updateDataSourceLabel(false, error);
            }
          }
          return;
        }
        // The upload succeeded but every flight was gated for being under 10
        // minutes ΓÇö report that plainly instead of as a "nothing found" error.
        if (analyzedFlightTotal === 0 && skippedShortFlightTotal > 0) {
          const message = shortFlightsSkippedSummary(skippedShortFlightTotal);
          jobs
            .filter((job) => job.status === "ready")
            .forEach((job) => {
              setUploadJobState(job, {
                statusLabel: "Short flight",
                message,
              });
            });
          setUploadDialogStatus(message);
          notifyUploadQueue({
            status: "ready",
            title: "Upload complete",
            message,
            targetAircraft,
            autoDismissMs: 12000,
          });
          return;
        }
        if (analyzedFlightTotal === 0 && discardedUnsupportedTotal > 0) {
          const message = discardedUnsupportedTotal === 1
            ? "No usable flight was found in that upload, so it was ignored."
            : `${discardedUnsupportedTotal} uploads had no usable flight data and were ignored.`;
          jobs
            .filter((job) => job.status === "ready")
            .forEach((job) => {
              setUploadJobState(job, {
                statusLabel: "Ignored",
                message,
              });
            });
          setUploadDialogStatus(message);
          notifyUploadQueue({
            status: "ready",
            title: "Upload ignored",
            message,
            targetAircraft,
            autoDismissMs: 12000,
          });
          return;
        }
        throw new Error(`${summary}. No selectable flight was found in the refreshed manifest.`);
      }
      const shortSkipNote = skippedShortFlightTotal > 0
        ? ` ${skippedShortFlightTotal} short flight${skippedShortFlightTotal === 1 ? "" : "s"} skipped.`
        : "";
      const noEngineDataNote = skippedNoEngineDataFlightTotal > 0
        ? ` ${skippedNoEngineDataFlightTotal} flight${skippedNoEngineDataFlightTotal === 1 ? "" : "s"} had no engine data and ${skippedNoEngineDataFlightTotal === 1 ? "was" : "were"} skipped.`
        : "";
      const failureNote = failedUploads.length ? ` ${failedUploads[0].message}` : "";
      const retainedNote = retainedLockedFlightIds.size > 0
        ? ` ${retainedLockedFlightsMessage(retainedLockedFlightIds.size)}`
        : "";
      const completionMessage = `${summary}.${shortSkipNote}${noEngineDataNote}${retainedNote}${failureNote}`;
      const userStayedOnSameFlight = (getSelectedFlight()?.id ?? "") === initialSelectedFlightId;
      if (userStayedOnSameFlight) {
        setUploadDialogStatus(`${completionMessage} Opening the newest flight...`);
        notifyUploadQueue({
          status: failedUploads.length ? "error" : "ready",
          title: failedUploads.length ? "Upload finished with issues" : "Upload complete",
          message: `${completionMessage} Opening the newest flight...`,
          targetAircraft: newestSelectableFlight.aircraft ?? targetAircraft,
          autoDismissMs: failedUploads.length ? 0 : 12000,
          forceOpen: failedUploads.length,
        });
        // markSeen: false - the app opened this flight, the user did not ask
        // for it, so it must keep its NEW tag until they open it themselves.
        await loadFlightLog(newestSelectableFlight, { activateTrial: true, markSeen: false });
        populateTrendAircraftSelect(newestSelectableFlight.aircraft);
        if (postSuccessPanel) {
          try {
            await setAircraftPanel(postSuccessPanel);
          } catch (error) {
            updateDataSourceLabel(false, error);
          }
        }
      } else {
        setUploadDialogStatus(completionMessage);
        notifyUploadQueue({
          status: failedUploads.length ? "error" : "ready",
          title: failedUploads.length ? "Upload finished with issues" : "Upload complete",
          message: completionMessage,
          targetAircraft: newestSelectableFlight.aircraft ?? targetAircraft,
          autoDismissMs: failedUploads.length ? 0 : 12000,
          forceOpen: failedUploads.length,
        });
      }
    } catch (error) {
      const message = error?.message || "Upload did not finish.";
      setUploadDialogStatus("");
      jobs
        .filter(uploadJobIsActive)
        .forEach((job) => {
          setUploadJobState(job, {
            status: "error",
            statusLabel: "Failed",
            message,
            error: message,
          }, { forceOpen: true });
        });
      notifyUploadQueue({
        status: "error",
        title: "Upload did not finish",
        message,
        targetAircraft,
        forceOpen: true,
      });
    } finally {
      syncUploadBeforeUnloadGuard(uploadHasUnsentQueueWork());
    }
  }

  async function uploadSelectedFiles(event) {
    event?.preventDefault?.();
    if (appReadOnlyMode()) {
      setUploadDialogError(readOnlyActionMessage("Uploading logs"));
      return;
    }
    const targetAircraft = uploadAircraftTargetValue();
    // "__new__" means the aircraft does not exist yet — the first accepted
    // POST creates it server-side, and the batch runner uses this to bring
    // the client up to date the moment that happens.
    const creatingNewAircraft = document.querySelector("#uploadAircraftSelect")?.value === "__new__";
    const files = selectedUploadFiles();
    const initialSelectedFlightId = getSelectedFlight()?.id ?? "";
    const postSuccessPanel = getUploadDialogPostSuccessPanel();
    setUploadDialogPostSuccessPanel("");
    if (!targetAircraft) {
      setUploadDialogError("Choose an aircraft before uploading.");
      return;
    }
    if (!files.length) {
      setUploadDialogError("Choose at least one `.ald`, `.csv`, `.jpi`, or `.zip` file.");
      return;
    }
    if (files.length > MAX_UPLOAD_FILES) {
      setUploadDialogError(`Uploads are limited to ${MAX_UPLOAD_FILES} files at a time. You selected ${files.length} — remove ${files.length - MAX_UPLOAD_FILES} and upload the rest in another batch.`);
      return;
    }
    const oversized = oversizedUploadFile(files);
    if (oversized) {
      setUploadDialogError(`${uploadFileDisplayName(oversized)} is larger than the ${Math.floor(MAX_UPLOAD_BYTES / (1024 * 1024))} MB upload limit.`);
      return;
    }
    // Plan capacity gate (enforced metered plans): a maxed-out plan blocks
    // the batch outright, and a batch larger than the remaining room opens
    // the capacity picker so the member chooses which flights to upload,
    // never more than the room available. The server enforces the same cap
    // on POST /api/uploads as the backstop.
    let selectedFiles = files;
    const capacity = await fetchUploadCapacity().catch(() => null);
    if (capacity?.limited) {
      // The server already counts uploads it has accepted but not yet
      // ingested; queue jobs still on this side of the wire are invisible
      // to it, so they claim their room here.
      const unsentQueued = uploadQueueJobs.filter(uploadJobIsUnsent).length;
      const remaining = Math.max(0, (Number(capacity.remaining) || 0) - unsentQueued);
      if (remaining <= 0) {
        setUploadDialogError(uploadAtFlightLimitMessage(capacity));
        return;
      }
      if (files.length > remaining) {
        const chosen = await chooseUploadFilesWithinCapacity(files, { ...capacity, remaining });
        if (!chosen?.length) return;
        selectedFiles = chosen;
      }
    }
    // On a plan that withholds flights, upload newest-first so the flights the
    // account keeps are the first ones the user sees land. Uploading in name
    // (chronological) order meant the first rows to appear were the oldest,
    // exactly the ones that end up marked "Plan limit".
    // A single file has nothing to order, so the plan lookup stays off the
    // common submit path.
    const withholding = selectedFiles.length > 1 && await accountWithholdsFlights().catch(() => false);
    const orderedFiles = withholding ? uploadFilesNewestFlightFirst(selectedFiles) : selectedFiles;
    const batchId = `upload-batch-${Date.now()}-${uploadJobSequence + 1}`;
    const jobs = orderedFiles.map((file) => createUploadJob({
      file,
      targetAircraft,
      uploadUrl: uploadUrlForFile(file),
      batchId,
    }));
    uploadQueueJobs.push(...jobs);
    setUploadDialogError("");
    setUploadDialogStatus("");
    closeUploadDialog();
    const completion = enqueueUploadBatch({
      id: batchId,
      jobs,
      targetAircraft,
      creatingNewAircraft,
      initialSelectedFlightId,
      postSuccessPanel,
    });
    return completion;
  }

  return {
    archiveFlightLogSource,
    removeFlightEntryFromList,
    removeSelectedFlightFromList,
    uploadAircraftMetadataParams,
    uploadUrlForFile,
    oversizedUploadFile,
    waitForDatabaseUploadIngest,
    uploadSelectedFiles,
  };
}
